import { FC, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Streamdown } from 'streamdown';
import type { RootState } from '@@store/index';
import { imageProxyBasePath, isImageProxyEnabled } from '@/common/imageProxy';
import './index.less';

interface MarkdownPropsType {
    content: string;
    className?: string;
}
export type MarkdownCompPropsType = MarkdownPropsType;

// 注意：Streamdown 不支持自定义组件，表格下载和代码复制功能暂时移除

const MarkdownIndex: FC<MarkdownPropsType> = memo((props) => {
    const { content, className = '' } = props;

    const curTheme = useSelector((state: RootState) => state.theme);

    // 预处理内容：保持列表分隔，同时修复编号对齐问题
    const processedContent = useMemo(() => {
        const trimmed = (content ?? '').trim();
        if (!trimmed) {
            return '';
        }

        const newlineNormalizationPatterns: RegExp[] = [
            // 有序列表（允许可选加粗起始）
            /\n[ \t]*\n+(?=\s*\d+\.\s*(\*\*|__)?)/g,
            // 无序列表 -, *, +
            /\n[ \t]*\n+(?=\s*[-*+]\s+)/g,
            // 列表项中的 Markdown 图片
            /\n[ \t]*\n+(?=\s*!\[[^\]]*]\([^)]*\))/g,
            // 列表项中的 Markdown 链接
            /\n[ \t]*\n+(?=\s*\[[^\]]+]\([^)]*\))/g,
            // HTML 图片
            /\n[ \t]*\n+(?=\s*<img\b)/gi
        ];

        let normalizedContent = trimmed;

        newlineNormalizationPatterns.forEach((pattern) => {
            normalizedContent = normalizedContent.replace(pattern, '\n');
        });

        return normalizedContent;
    }, [ content ]);

    const { allowedImagePrefixes, defaultOrigin } = useMemo(() => {
        if (!isImageProxyEnabled) {
            return { allowedImagePrefixes: undefined, defaultOrigin: undefined };
        }

        const prefixes: string[] = [ `${imageProxyBasePath}/image-proxy` ];
        let origin: string | undefined;

        if (typeof window !== 'undefined' && window.location?.origin) {
            origin = window.location.origin.replace(/\/$/, '');
            prefixes.push(`${origin}${imageProxyBasePath}/image-proxy`);
        }

        return {
            allowedImagePrefixes: prefixes,
            defaultOrigin: origin
        };
    }, []);

    return (
        <Streamdown
            className={`base-markdown-streamdown ${curTheme}-base-markdown-streamdown ${className}`}
            key={curTheme}
            parseIncompleteMarkdown={true}
            controls={false}
            allowedImagePrefixes={allowedImagePrefixes}
            defaultOrigin={defaultOrigin}
        >
            {processedContent}
        </Streamdown>
    );
});

export default MarkdownIndex;
